import { compact, first, isArray, isEmpty, size } from 'es-toolkit/compat';
import type { AgentSession } from 'interfaces/agentSession';
import { stringify } from 'qs';
import { Location } from 'react-router-dom';
import { encodeQueryParams, JsonParam, StringParam } from 'use-query-params';

import { filtersQueryParamsSchema } from 'utils/useEncodedFilters/constants';
import { encodeQueryParamsUsingSchema } from 'utils/useEncodedFilters/helpers';

interface ReportFilters {
  studyId?: string | string[] | null;
}

interface ReportQueryParams {
  labId?: string | null;
  filters?: ReportFilters | null;
  sessionId?: string | null;
  studyIdsOverride?: string[];
}

interface PreviousLocation {
  pathname: string;
  search: string;
}

interface ExistingNavigationState {
  previousLocation?: PreviousLocation;
  cameFrom?: string;
}

export interface StudyReportRouteState extends ExistingNavigationState {
  reportContext: {
    labId?: string | null;
    selectedStudyIds: string[];
  };
  draftConversation?: {
    initialUserPrompt?: string;
    autoSubmit?: boolean;
  };
}

export const REPORT_CANVAS_ROUTE_PATH = '/report-canvas';

export const isReportCanvasRoutePath = (pathname: string) =>
  pathname === REPORT_CANVAS_ROUTE_PATH || pathname.startsWith(`${REPORT_CANVAS_ROUTE_PATH}/`);

const getSelectedStudyIds = (filters?: ReportFilters | null) => {
  const studyId = filters?.studyId;

  return compact(isArray(studyId) ? studyId : studyId ? [studyId] : []);
};

const getSessionSelectedStudyIds = (session?: Pick<AgentSession, 'studyIds' | 'sessionData'> | null) => {
  const reportStudyIds = session?.sessionData?.reportState?.studyIds || [];
  return compact(!isEmpty(reportStudyIds) ? reportStudyIds : session?.studyIds || []);
};

export const buildStudyReportSearch = ({ labId, filters, sessionId, studyIdsOverride }: ReportQueryParams) => {
  const selectedStudyIds = filters ? getSelectedStudyIds(filters) : [];
  const effectiveStudyIds = studyIdsOverride && studyIdsOverride.length > 0 ? studyIdsOverride : selectedStudyIds;
  const reportFilters = !isEmpty(effectiveStudyIds)
    ? { studyId: size(effectiveStudyIds) === 1 ? first(effectiveStudyIds) : effectiveStudyIds }
    : null;

  const encodedSearch = encodeQueryParamsUsingSchema(
    {
      labId: labId || undefined,
      page: 1,
      filters: reportFilters,
      features: null,
      clinicalData: null,
      queryId: null,
      searchTerm: null,
      caseIdsToInclude: [],
      slideIdsToInclude: [],
      slidesMode: false,
    },
    filtersQueryParamsSchema,
    { arrayFormat: 'repeat' }
  );

  const searchParams = new URLSearchParams(encodedSearch);
  if (sessionId) {
    searchParams.set('sessionId', sessionId);
  }

  const serializedSearch = searchParams.toString();
  return serializedSearch ? `?${serializedSearch}` : '';
};

export const buildStudyReportRouteState = ({
  filters,
  labId,
  initialUserPrompt,
  location,
  existingState,
}: {
  filters?: ReportFilters | null;
  labId?: string | null;
  initialUserPrompt?: string;
  location: Location;
  existingState?: ExistingNavigationState | null;
}): StudyReportRouteState => {
  const selectedStudyIds = getSelectedStudyIds(filters);
  const previousLocation = existingState?.previousLocation || {
    pathname: location.pathname,
    search: location.search,
  };

  return {
    ...existingState,
    cameFrom: existingState?.cameFrom || previousLocation.pathname,
    previousLocation,
    reportContext: {
      labId,
      selectedStudyIds,
    },
    draftConversation: initialUserPrompt ? { initialUserPrompt } : undefined,
  };
};

export const resolveStudyReportSelectedStudyIds = ({
  filters,
  session,
}: {
  filters?: ReportFilters | null;
  session?: Pick<AgentSession, 'studyIds' | 'sessionData'> | null;
}) => {
  const selectedStudyIds = getSelectedStudyIds(filters);
  return !isEmpty(selectedStudyIds) ? selectedStudyIds : getSessionSelectedStudyIds(session);
};

export const getStudyReportReturnLocation = (
  state?: ExistingNavigationState | null,
  session?: Pick<AgentSession, 'sessionData'> | null
): PreviousLocation => {
  return (
    state?.previousLocation ||
    session?.sessionData?.navigation?.returnToOnClose || {
      pathname: '/',
      search: '',
    }
  );
};

export const buildStudyReportCanvasCloseLocation = ({
  selectedStudyIds,
  labId,
  keepDrawerOpen = false,
}: {
  selectedStudyIds: string[];
  labId?: string | null;
  keepDrawerOpen?: boolean;
}): PreviousLocation => {
  if (!isEmpty(selectedStudyIds)) {
    const encodedQuery = encodeQueryParams(
      { filters: JsonParam, labId: StringParam },
      {
        filters: { studyId: size(selectedStudyIds) === 1 ? first(selectedStudyIds) : selectedStudyIds },
        labId: labId || undefined,
      }
    );
    const searchParams = new URLSearchParams(stringify(encodedQuery));

    if (keepDrawerOpen) {
      searchParams.set('copilotDrawerOpen', '1');
    }

    const serializedSearch = searchParams.toString();
    return {
      pathname: '/procedures',
      search: serializedSearch ? `?${serializedSearch}` : '',
    };
  }

  const searchParams = new URLSearchParams();
  if (labId) {
    searchParams.set('labId', labId);
  }
  if (keepDrawerOpen) {
    searchParams.set('copilotDrawerOpen', '1');
  }

  const serializedSearch = searchParams.toString();
  return {
    pathname: '/',
    search: serializedSearch ? `?${serializedSearch}` : '',
  };
};
